import React, { useState, useMemo, useEffect } from "react";
import { Box, Typography, CircularProgress, Tooltip, Paper, IconButton } from "@mui/material";
import { useGetScenarioCardsQuery, useCalculateMetricsQuery, ScenarioCard } from "@/services/dashboard/sm-api";
import ScenarioList from "./components/scenario-list";
import ScenarioDetail from "./components/scenario-detail";
import CreateScenarioForm from "./components/create-scenario";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import EditIcon from "@/assets/svgs/edit.svg";
import DeleteIcon from "@/assets/svgs/garbage.svg";
import CloseIcon from "@mui/icons-material/Close";

const ScenarioModelingPage: React.FC = () => {
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient, areClientsLoading } = useAppSelector((state) => state.client);
  const { selectedTenant,areTenantsLoading } = useAppSelector((state) => state.tenantManagement);

  // Clear selected scenario when client changes
  useEffect(() => {
    setSelectedScenario(null);
  }, [selectedClient?._id]);

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  // For client accounts, use the user's tenantId as the clientId
  const isClientAccount = useMemo(() => {
    return user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT;
  }, [user]);

  const isAdmin = useMemo(() => {
    return user?.userRole === roles.TDADMIN;
  }, [user, selectedTenant]);

  const isTDAdmin = useMemo(() => {
    return isAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const commonOptions = useMemo(() => {
    if (isTDAdmin) {
      return {
        skip: !selectedTenant,
        refetchOnMountOrArgChange: true,
      };
    }
    return {
      skip: canSelectClients && !selectedClient,
      refetchOnMountOrArgChange: true,
    };
  }, [canSelectClients, selectedClient]);

  // Get the clientId, ensuring it's not an empty string
  // For client accounts, use the user's tenantId
  // For SP accounts, use the selectedClient._id
  const clientId = useMemo(() => {
    if (isClientAccount && user?.tenantId) {
      return user.tenantId;
    }
    if(isTDAdmin && selectedTenant?._id){
      return selectedTenant?._id;
    }
    return selectedClient?._id || undefined;
  }, [isClientAccount, user, selectedClient, selectedTenant]);

  // Debug logs
  useEffect(() => {
    console.log("User role:", user?.userRole);
    console.log("Is client account:", isClientAccount);
    console.log("User tenantId:", user?.tenantId);
    console.log("Selected client:", selectedClient);
    console.log("Using clientId:", clientId);
  }, [user, isClientAccount, selectedClient, clientId]);

  const { data: scenariosData,isFetching: isFetchingScenarios, error: scenariosError } = useGetScenarioCardsQuery(
    { 
      clientId: user?.userRole === roles.TDCLIENT ? user?._id : clientId || "",
      ...(isAdmin && { tenantId: selectedTenant?._id }),
      pageNo: 1,
      pageSize: 10
    },
    {
      ...commonOptions,
      // Skip the query if there's no clientId
      skip: isTDAdmin ? false : commonOptions.skip || !clientId
    }
  );

  // Debug logs for API response
  useEffect(() => {
    console.log("Scenarios data:", scenariosData);
    console.log("Scenarios error:", scenariosError);
  }, [scenariosData, scenariosError]);

  const { data: metricsData, isLoading: isLoadingMetrics, refetch: refetchMetrics } = useCalculateMetricsQuery(
    { scenarioId: selectedScenario ?? "" },
    { skip: !selectedScenario }
  );

  const handleScenarioDeleted = () => {
    setSelectedScenario(null);
  };

  if (areClientsLoading || isFetchingScenarios || areTenantsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (scenariosError) {
    return (
      <Box p={3}>
        <Typography color="error">Error loading scenarios</Typography>
      </Box>
    );
  }

  const scenarios = scenariosData?.scenarioCards.data || [];
  const selectedScenarioData = scenarios.find(s => s._id === selectedScenario);

  // Create a component for HTS codes with tooltip
  const HTSCodesWithTooltip = ({ htsCodes }: { htsCodes: string[] }) => {
    // Force tooltip to show when there are more than 2 codes
    const shouldShowTooltip = htsCodes.length > 2;
    
    // Determine what text to display
    const displayText = htsCodes.length === 0 
      ? "All" 
      : htsCodes.length > 2 
        ? `${htsCodes.slice(0, 2).join(", ")}...` 
        : htsCodes.join(", ");
    
    // Full text for the tooltip
    const fullText = htsCodes.length === 0 ? "All" : htsCodes.join(", ");
    
    return (
      <Box 
        sx={{ 
          color: "#101828", 
          fontSize: "0.85rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: shouldShowTooltip ? "pointer" : "default",
          textDecoration: shouldShowTooltip ? "underline" : "none",
          maxWidth: "100%"
        }}
      >
        <Tooltip 
          title={`HTS Codes: ${fullText}`} 
          placement="top-start"
          disableHoverListener={!shouldShowTooltip}
        >
          <div>HTS Codes: {displayText}</div>
        </Tooltip>
      </Box>
    );
  };

  return (
          <Box>
      <Box mb={4}>
        <CreateScenarioForm 
          onScenarioCreated={(scenario: ScenarioCard) => setSelectedScenario(scenario._id)}
          clientId={clientId}
          isAdmin={isAdmin}
          tenantId={selectedTenant?._id}
        />
      </Box>

      <Box mb={4}>
        <ScenarioList
          scenarios={scenarios}
          selectedScenarioId={selectedScenario}
          onScenarioSelect={setSelectedScenario}
        />
      </Box>

      {selectedScenarioData && (
        <Box mt={0}>
          {/* Duty Increase Details Box with buttons */}
          <Paper
            sx={{
              p: 1.75,
              borderRadius: "10px",
              border: "1px solid #E6E7E7",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: "#101828", mb: 0.75, fontSize: "1.15rem" }}>
                  Duty Increase
          </Typography>
                {isLoadingMetrics ? (
                  <CircularProgress size={20} />
                ) : metricsData?.calculateScenarioMetrics ? (
                  <Typography variant="h2" sx={{ color: "#B42318", fontWeight: 600, mb: 0.75, fontSize: "1.9rem" }}>
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(metricsData.calculateScenarioMetrics.dutyAmount)}
                  </Typography>
                ) : null}
                <Typography variant="h6" sx={{ color: "#101828", mb: 0.75, fontSize: "0.85rem" }}>
                  Rate: <span style={{ color: "#B42318" }}>{selectedScenarioData.rate}%</span> | Countries: {selectedScenarioData.country_list.join(", ")}
                    </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <HTSCodesWithTooltip htsCodes={selectedScenarioData.hts_codes} />
                </Box>
                {selectedScenarioData.dateRange && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="h6" sx={{ color: "#101828", fontSize: "0.85rem" }}>
                      Date Range: {
                        new Date(selectedScenarioData.dateRange.start).getFullYear() === 1999 ? 
                        "All Dates" : 
                        `${new Date(selectedScenarioData.dateRange.start).toLocaleDateString()} - ${new Date(selectedScenarioData.dateRange.end).toLocaleDateString()}`
                      }
                    </Typography>
                  </Box>
                )}
                </Box>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <IconButton 
                  onClick={() => setIsEditDialogOpen(true)} 
                  sx={{ color: "#475467", padding: 1 }}
                >
                  <img src={EditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
                </IconButton>
                <IconButton 
                  onClick={() => setIsDeleteDialogOpen(true)} 
                  sx={{ color: "#475467", padding: 1 }}
                >
                  <img src={DeleteIcon} alt="Delete" style={{ width: 20, height: 20 }} />
                </IconButton>
                <IconButton 
                  onClick={() => setSelectedScenario(null)} 
                  sx={{ color: "#475467", padding: 1 }}
                >
                  <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
          </Box>
        </Box>
          </Paper>

          {/* Recommendations */}
          <ScenarioDetail
            id="scenario-detail"
            scenario={selectedScenarioData}
            metrics={metricsData?.calculateScenarioMetrics}
            isLoadingMetrics={isLoadingMetrics}
            onClose={() => setSelectedScenario(null)}
            onDeleted={handleScenarioDeleted}
            onMetricsUpdate={() => refetchMetrics()}
            isCompact={true}
            isSlim={false}
            showOnlyRecommendations={true}
            hideButtons={true}
            isEditDialogOpen={isEditDialogOpen}
            setIsEditDialogOpen={setIsEditDialogOpen}
            isDeleteDialogOpen={isDeleteDialogOpen}
            setIsDeleteDialogOpen={setIsDeleteDialogOpen}
          />
        </Box>
      )}
        </Box>
  );
};

export default ScenarioModelingPage;
